import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-page">
      <div className="privacy-container">

        <header className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: December 2025</p>
        </header>

        <section className="privacy-content">
          <div className="privacy-intro">
            <h2>Your Privacy Matters to Reda Labs</h2>
            <p>
              At Reda Labs, LLC ("Reda Labs," "we," "our," or "us"), we are committed to protecting
              your privacy and handling your personal information with care and transparency. This
              Privacy Policy explains what information we collect, why we collect it, how we use it,
              and the choices you have regarding your data.
            </p>
            <p>
              We only collect and use personal information as necessary to deliver our products,
              services, applications, and communications. If you have questions at any time, our
              Privacy Team is here to help. Contact us at{' '}
              <a href="mailto:support@redalabs.com">support@redalabs.com</a>.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Information We Collect</h2>
            <p>
              We collect information to provide you with better services and improve your experience.
              The types of personal information we may collect include:
            </p>
            <ul>
              <li>Name and contact details (email address, phone number)</li>
              <li>Account credentials and authentication information</li>
              <li>Billing information, such as billing address and transaction details, which is processed by third-party payment providers</li>
              <li>Device information (device type, operating system, unique identifiers)</li>
              <li>Usage data and analytics (how you interact with our services)</li>
              <li>Location data (approximate location based on IP address)</li>
              <li>Communications you send to us (support requests, feedback)</li>
              <li>Information from third-party platforms when you connect accounts</li>
              <li>Job application information (for candidates applying to positions)</li>
            </ul>
            <p>
              Reda Labs does not store full credit card numbers.
            </p>
          </div>

          <div className="privacy-section">
            <h2>How We Collect Your Information</h2>
            <p>We collect information through various methods:</p>
            <ul>
              <li>
                <strong>Direct Collection:</strong> When you create an account, make a purchase,
                subscribe to our services, or contact our support team
              </li>
              <li>
                <strong>Automatic Collection:</strong> Through cookies, web beacons, and similar
                tracking technologies when you use our websites and applications
              </li>
              <li>
                <strong>Third-Party Sources:</strong> From publicly available databases, social
                media platforms, or business partners when permitted by law
              </li>
              <li>
                <strong>Mobile Applications:</strong> When you download and use our iOS or Android
                applications from the Apple App Store or Google Play Store
              </li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>Cookies and Tracking Technologies</h2>
            <p>
              Our services use cookies and similar technologies to enhance your experience and
              collect usage data. These technologies help us:
            </p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Understand how you interact with our services</li>
              <li>Measure and improve service performance</li>
              <li>Deliver relevant content and, where applicable, marketing communications</li>
              <li>Prevent fraud and enhance security</li>
            </ul>
            <p>
              The data collected includes metadata, log files, cookie identifiers, page load times,
              IP addresses, browser types, device information, and interaction patterns. You can
              manage cookie preferences through your browser settings, though some features may be
              limited if cookies are disabled.
            </p>
          </div>

          <div className="privacy-section">
            <h2>How We Use Your Information</h2>
            <p>
              We believe in data minimization and only use your information for legitimate purposes:
            </p>
            <ul>
              <li>
                <strong>Service Delivery:</strong> To provide, maintain, and improve our products
                and services
              </li>
              <li>
                <strong>Communication:</strong> To send you updates, notifications, marketing
                materials, and respond to your inquiries
              </li>
              <li>
                <strong>Personalization:</strong> To customize your experience and provide relevant
                recommendations
              </li>
              <li>
                <strong>Security:</strong> To detect, prevent, and address fraud, security risks,
                and technical issues
              </li>
              <li>
                <strong>Analytics:</strong> To understand usage patterns and optimize service
                performance
              </li>
              <li>
                <strong>Legal Compliance:</strong> To comply with applicable laws, regulations,
                and legal processes
              </li>
              <li>
                <strong>Business Operations:</strong> To conduct internal business operations,
                including accounting, billing, and record-keeping
              </li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>Information Sharing and Disclosure</h2>
            <p>
              We do not sell your personal information. We may share your data with:
            </p>
            <ul>
              <li>
                <strong>Service Providers:</strong> Trusted third parties who assist us in
                operating our business, such as payment processors, hosting providers, customer
                support platforms, and analytics services
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with mergers, acquisitions,
                asset sales, or other corporate transactions
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, court order, or
                government request, or to protect our rights and prevent illegal activities
              </li>
              <li>
                <strong>With Your Consent:</strong> When you explicitly authorize us to share
                your information with specific third parties
              </li>
            </ul>
            <p>
              All third-party service providers are contractually required to protect your
              information and use it only for the purposes we specify.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your personal information
              from unauthorized access, disclosure, alteration, and destruction. We take reasonable administrative, technical, and organizational measures, which may include:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and vulnerability testing</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection and privacy</li>
              <li>Incident response procedures</li>
            </ul>
            <p>
              While we strive to protect your information, no security system is completely
              impenetrable. We encourage you to use strong passwords and keep your account
              credentials confidential.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Data Retention</h2>
            <p>
              We retain your personal information only as long as necessary to:
            </p>
            <ul>
              <li>Provide you with our services</li>
              <li>Comply with legal obligations and resolve disputes</li>
              <li>Enforce our agreements and policies</li>
              <li>Maintain accurate business and financial records</li>
            </ul>
            <p>
              When your information is no longer needed, we securely delete or anonymize it in
              accordance with our data retention policies and applicable laws.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Your Rights and Choices</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal
              information:
            </p>
            <ul>
              <li>
                <strong>Access:</strong> Request a copy of the personal information we hold about you
              </li>
              <li>
                <strong>Correction:</strong> Update or correct inaccurate or incomplete information
              </li>
              <li>
                <strong>Deletion:</strong> Request deletion of your personal information, subject
                to legal and contractual obligations
              </li>
              <li>
                <strong>Portability:</strong> Request your data in a structured, machine-readable
                format
              </li>
              <li>
                <strong>Opt-Out:</strong> Unsubscribe from marketing communications or opt out of
                certain data processing activities
              </li>
              <li>
                <strong>Objection:</strong> Object to certain processing activities based on
                legitimate interests
              </li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:support@redalabs.com">support@redalabs.com</a>. We will respond
              to your request within a reasonable timeframe as required by law.
            </p>
          </div>

          <div className="privacy-section">
            <h2>International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your
              country of residence. These countries may have different data protection laws. When
              we transfer your data internationally, we ensure appropriate safeguards are in place,
              such as:
            </p>
            <ul>
              <li>Standard contractual clauses approved by relevant authorities</li>
              <li>Adequacy decisions recognizing equivalent data protection</li>
              <li>Your explicit consent when required</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>Children's Privacy</h2>
            <p>
              Our services are not intended for children under the age of 13 (or under 16 where required by law). We do not
              knowingly collect personal information from children. If you believe we have
              inadvertently collected information from a minor, please contact us immediately at{' '}
              <a href="mailto:support@redalabs.com">support@redalabs.com</a>, and we will take
              prompt action to delete such information.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Third-Party Links and Services</h2>
            <p>
              Our services may contain links to third-party websites, applications, or services.
              We are not responsible for the privacy practices or content of these third parties.
              We encourage you to review their privacy policies before providing any personal
              information.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Marketing Communications</h2>
            <p>
              We may send you promotional emails, text messages, or other communications about our
              products and services. You can opt out at any time by:
            </p>
            <ul>
              <li>Clicking the "unsubscribe" link in our emails</li>
              <li>Adjusting your account notification preferences</li>
              <li>Contacting us at <a href="mailto:support@redalabs.com">support@redalabs.com</a></li>
            </ul>
            <p>
              Even if you opt out of marketing communications, we may still send you transactional
              or service-related messages necessary for your use of our services.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Do Not Track Signals</h2>
            <p>
              Some web browsers transmit "Do Not Track" signals. Currently, there is no industry
              standard for how to respond to these signals. We do not alter our data collection
              and use practices in response to Do Not Track signals. For more information, visit{' '}
              <a href="https://www.allaboutdnt.com" target="_blank" rel="noopener noreferrer">
                www.allaboutdnt.com
              </a>.
            </p>
          </div>

          <div className="privacy-section">
            <h2>California Privacy Rights</h2>
            <p>
              If you are a California resident, you have additional rights under the California
              Consumer Privacy Act (CCPA), including the right to know what personal information
              we collect, the right to delete your information, and the right to opt out of the
              sale of your personal information (we do not sell personal information).
            </p>
            <p>
              We will not discriminate against you for exercising your privacy rights. This means
              we will not deny services, charge different prices, or provide a different quality
              of service solely because you exercised your rights.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices,
              technology, legal requirements, or other factors. When we make material changes, we
              will notify you by:
            </p>
            <ul>
              <li>Posting the updated policy on this page with a new "Last Updated" date</li>
              <li>Sending you an email notification (if you have an account with us)</li>
              <li>Displaying a prominent notice on our website or applications</li>
            </ul>
            <p>
              We encourage you to review this Privacy Policy regularly to stay informed about how
              we protect your information.
            </p>
          </div>

          <div className="privacy-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions, concerns, or complaints about our Privacy Policy or handling of your personal data, contact us at <a href="mailto:support@redalabs.com">support@redalabs.com</a>.
            </p>
            <p>
              Reda Labs, LLC is the data controller for <a href="https://www.redalabs.com" target="_blank" rel="noopener noreferrer">www.redalabs.com</a>
            </p>
          </div>
        </section>
      </div>

      <footer className="footer">
        <nav className="footer-nav">
          <Link to="/">Home</Link>
          <span className="separator">|</span>
          <Link to="/terms">Terms of Service</Link>
          <span className="separator">|</span>
          <Link to="/privacy">Privacy Policy</Link>
          <span className="separator">|</span>
          <Link to="/support">Support</Link>
        </nav>
        <p className="copyright">© 2025 Reda Labs, LLC</p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
